import React, { useMemo } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { LOGO_HITAM_SDE_BASE64 } from '@/Components/production/logoHitamBase64';

/**
 * Single SVG Label Component
 */
export function SingleLabelSVG({
  labelSize,
  brandName,
  recipientName,
  recipientAddress,
  poNumber,
  variantName,
  qtyPcs,
  tglProduksi,
  expDate,
  dusKe,
  catatanDus,
  qrTargetUrl,
  isMixBox,
  isLebihanBox,
  breakdownItems
}) {
  const isBesar = labelSize === 'besar';
  const viewBox = isBesar ? "0 0 198.43 212.6" : "0 0 153.77 164.41";

  // Helper render catatan dus & rincian isi campuran (di area kiri bawah y=165.33 s/d 203.18)
  const renderCatatanSection = (xPos = 12.45) => {
    if (isMixBox || isLebihanBox) {
      const title = isMixBox ? 'CATATAN ISI MIX:' : 'CATATAN ISI LEBIHAN:';
      const itemsList = (breakdownItems && breakdownItems.length > 0)
        ? breakdownItems.map(it => `${it.qty} pcs ${it.name}`)
        : [(catatanDus || '').replace(/^(\[DUS CAMPURAN\]|\[DUS LEBIHAN.*?\]|Isi Campuran:|Isi Lebihan:|Catatan\s*:\s*)/i, '').trim()];

      const combinedStr = itemsList.join(' · ');

      if (combinedStr.length <= 44) {
        return (
          <g>
            <text className="st-bold" x={xPos} y="174" fontSize="3.1" fill="#111827">
              {title}
            </text>
            <text className="st-extrabold" x={xPos} y="180" fontSize="3.3" fill="#1e293b">
              {combinedStr}
            </text>
          </g>
        );
      } else {
        const mid = Math.ceil(itemsList.length / 2);
        const l1 = itemsList.slice(0, mid).join(' · ');
        const l2 = itemsList.slice(mid).join(' · ');
        return (
          <g>
            <text className="st-bold" x={xPos} y="172" fontSize="2.9" fill="#111827">
              {title}
            </text>
            <text className="st-extrabold" x={xPos} y="178" fontSize="3.0" fill="#1e293b">
              {l1}
            </text>
            <text className="st-extrabold" x={xPos} y="184" fontSize="3.0" fill="#1e293b">
              {l2 || ''}
            </text>
          </g>
        );
      }
    }

    const cleanCatatan = catatanDus ? (catatanDus.startsWith('Catatan :') ? catatanDus : `Catatan : ${catatanDus}`) : 'Catatan : -';
    if (cleanCatatan.length > 44) {
      const words = cleanCatatan.split(' ');
      let line1 = '';
      let line2 = '';
      words.forEach(w => {
        if ((line1 + ' ' + w).length <= 42 && !line2) {
          line1 = line1 ? `${line1} ${w}` : w;
        } else {
          line2 = line2 ? `${line2} ${w}` : w;
        }
      });
      return (
        <g>
          <text className="st-italic" x={xPos} y="173" fontSize="3.2">
            {line1}
          </text>
          <text className="st-italic" x={xPos} y="179" fontSize="3.2">
            {line2}
          </text>
        </g>
      );
    }

    return (
      <text className="st-italic" x={xPos} y="174" fontSize="3.77">
        {cleanCatatan}
      </text>
    );
  };

  // Reusable label body according to Mas Fadli's new Illustrator layout
  const renderLabelBody = () => (
    <g>
      {/* Main Border Box */}
      <rect className="st-line" x="8.96" y="9.6" width="180.51" height="193.4" />

      {/* Dividing Grid Lines */}
      <line className="st-line" x1="62.74" y1="9.74" x2="62.74" y2="63.9" />
      <line className="st-line" x1="62.78" y1="47.64" x2="189.51" y2="47.64" />
      <line className="st-line" x1="8.97" y1="63.81" x2="189.48" y2="63.81" />
      <line className="st-line" x1="8.96" y1="92.25" x2="189.47" y2="92.25" />
      <line className="st-line" x1="99.65" y1="92.45" x2="99.65" y2="203.18" />

      {/* Left Column Lines */}
      <line className="st-line" x1="8.95" y1="110.72" x2="99.5" y2="110.72" />
      <line className="st-line" x1="60.19" y1="110.81" x2="60.19" y2="129.62" />
      <line className="st-line" x1="8.95" y1="129.83" x2="99.63" y2="129.81" />
      <line className="st-line" x1="8.95" y1="165.33" x2="99.63" y2="165.31" />

      {/* Right Column Lines */}
      <line className="st-line" x1="99.67" y1="110.76" x2="189.48" y2="110.76" />
      <line className="st-line" x1="99.76" y1="129.72" x2="189.46" y2="129.72" />
      <line className="st-line" x1="99.37" y1="148.82" x2="189.46" y2="148.83" />

      {/* SD Monogram Logo (Menggunakan shared symbol cache) */}
      <use href="#satala-sd-logo-global" xlinkHref="#satala-sd-logo-global" x="18" y="15" width="38" height="42" />

      {/* DARI Section */}
      <text className="st-italic" x="69.42" y="19.79" fontSize="5.49">DARI:</text>
      <text className="st-extrabold" x="69.42" y="26.4" fontSize="5.49">PT. SATALA DERMATECH ESSENTIAL</text>
      <text className="st-semibold" x="69.42" y="31.56" fontSize="4.55">
        <tspan x="69.42" dy="0">Jl. Aster Kav, Paspampres, Kota Batu,</tspan>
        <tspan x="69.42" dy="5.46">Kec. Ciomas, Kabupaten Bogor,</tspan>
        <tspan x="69.42" dy="5.46">Jawa Barat 16610</tspan>
      </text>

      {/* NAMA BRAND */}
      <text className="st-extrabold" x="126" y="57.55" fontSize="6.5" textAnchor="middle">{brandName}</text>

      {/* KEPADA Section */}
      <text className="st-italic" x="14.25" y="73.31" fontSize="5.49">KEPADA:</text>
      <text className="st-extrabold" x="42.28" y="73.31" fontSize="5.49">{recipientName}</text>
      <text className="st-semibold" x="42.3" y="78.92" fontSize="4.2">
        {recipientAddress.length > 55 ? `${recipientAddress.substring(0, 52)}...` : recipientAddress}
      </text>

      {/* NOMOR PO Section */}
      <text className="st-italic" x="14.28" y="100.82" fontSize="5.49">NOMOR PO:</text>
      <text className="st-extrabold" x="13.74" y="106.6" fontSize={poNumber.length > 24 ? "4.0" : "4.8"}>
        {poNumber}
      </text>

      {/* TANGGAL PRODUKSI (Left Sub-column) */}
      <text className="st-italic" x="13.08" y="115.8" fontSize="3.77">TANGGAL PRODUKSI:</text>
      <text className="st-bold" x="35" y="124" fontSize="4.55" textAnchor="middle">{tglProduksi}</text>

      {/* EXP DATE (Right Sub-column) */}
      <text className="st-italic" x="63.16" y="115.8" fontSize="3.77">EXP DATE:</text>
      <text className="st-bold" x="80" y="124" fontSize="4.55" textAnchor="middle">{expDate}</text>

      {/* FRAGILE, KEEP DRY (UMBRELLA) & THIS WAY UP (ARROWS) VECTORS */}
      <g>
        {/* Payung / Umbrella */}
        <path fill="#231f20" d="M40.24,133.89v8.26c-.02,1.36-.61,2.57-1.5,3.55-1.05,1.1-2.41,1.77-3.94,1.93v5.46c1.26.15,2.48.51,3.6,1.13.68.37,1.3.81,1.86,1.37h-13.17c.49-.5,1.01-.88,1.6-1.23,1.15-.68,2.41-1.09,3.75-1.24v-5.49c-1.21-.16-2.33-.62-3.27-1.38-1.28-1.02-2.01-2.49-2.06-4.14v-8.22h4.03l.96,1.47h-2.62l2.43,3.3h-2.43l5.53,6.08-2.37-4.37,2.92-.02-2.18-3.44h1.95l-1.92-3.03h6.83Z" />
        {/* Panah Atas 1 */}
        <polygon fill="#231f20" points="58.8 155.6 58.8 140.4 55.61 140.39 56.43 139.44 61.62 133.68 67.59 140.39 64.01 140.4 64.01 155.58 58.8 155.6" />
        {/* Panah Atas 2 */}
        <polygon fill="#231f20" points="79.32 155.59 74.13 155.59 74.13 140.4 70.93 140.39 71.52 139.7 76.92 133.68 79.34 136.41 82.91 140.39 79.32 140.4 79.32 155.59" />
        {/* Bar bawah panah */}
        <rect fill="#231f20" x="53.6" y="157" width="31.45" height="3.71" />
        {/* Vectorized FRAGILE Lettering */}
        <polygon fill="#231f20" points="25.88 158.88 25.87 159.61 24.27 159.62 24.26 160.85 23.34 160.84 23.34 157.36 26.03 157.36 26.04 158.09 24.27 158.09 24.27 158.88 25.88 158.88" />
        <path fill="#231f20" d="M29.68,160.83l-1.02.02-.49-.94h-.68v.93h-.92v-3.48h1.64c.39,0,.76.12,1.06.36.53.56.44,1.65-.3,1.99l.71,1.11h0ZM28.31,159.15c.34,0,.45-.37.41-.61-.05-.31-.29-.46-.61-.45h-.62v1.08s.82-.02.82-.02Z" />
        <path fill="#231f20" d="M32.58,160.23h-1.5l-.23.62h-.95l1.48-3.48h.95l1.44,3.47h-.96l-.23-.61ZM32.32,159.51l-.47-1.22-.48,1.22h.95Z" />
        <path fill="#231f20" d="M36.36,159.4l-.36-.03v-.33h1.17v1.42c-.6.39-1.35.57-2.07.37-.79-.22-1.27-.93-1.27-1.72s.52-1.49,1.32-1.72c.72-.2,1.48-.03,2.05.46l-.52.62c-.21-.15-.42-.3-.67-.36-.63-.15-1.18.26-1.24.88-.04.42.13.79.46,1,.35.21.82.17,1.14-.05v-.54h0Z" />
        <rect fill="#231f20" x="37.82" y="157.36" width=".91" height="3.49" />
        <polygon fill="#231f20" points="41.89 160.07 41.89 160.84 39.46 160.85 39.45 157.36 40.35 157.36 40.35 160.07 41.89 160.07" />
        <polygon fill="#231f20" points="44.95 159.45 43.31 159.45 43.32 160.1 45.19 160.1 45.19 160.85 42.39 160.85 42.39 157.36 45.16 157.36 45.16 158.09 43.31 158.09 43.31 158.73 44.94 158.73 44.95 159.45" />
      </g>

      {/* Bottom Left: CATATAN DUS */}
      {renderCatatanSection(12.45)}

      {/* Right Top: NAMA VARIANT */}
      <text className="st-italic" x="102.66" y="96.25" fontSize="2.9">NAMA VARIANT:</text>
      <text
        className="st-extrabold"
        x="144"
        y="104.64"
        fontSize={(variantName || '').length > 28 ? "3.6" : (variantName || '').length > 20 ? "4.4" : "5.2"}
        textAnchor="middle"
      >
        {variantName || '-'}
      </text>

      {/* Right Mid: JUMLAH */}
      <text className="st-italic" x="102.66" y="115.23" fontSize="2.9">JUMLAH:</text>
      <text className="st-extrabold" x="144" y="123.63" fontSize="5.2" textAnchor="middle">
        {Number(qtyPcs).toLocaleString('id-ID')} PCS DUS
      </text>

      {/* Right Bottom 1: DUS KE */}
      <text className="st-italic" x="102.48" y="133.56" fontSize="2.9">DUS KE:</text>
      <text className="st-extrabold" x="144" y="142.39" fontSize="9.51" textAnchor="middle">{dusKe}</text>

      {/* Right Bottom 2: DICEK OLEH (KOSONG UNTUK STEMPEL QC) */}
      <text className="st-italic" x="103.47" y="154.89" fontSize="3.77">DICEK OLEH:</text>
    </g>
  );

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={viewBox}
      width="100%"
      height="100%"
      style={{ display: 'block', backgroundColor: '#ffffff' }}
    >
      {isBesar ? (
        /* ================= UKURAN BESAR (7 x 7.5 cm) ================= */
        renderLabelBody()
      ) : (
        /* ================= UKURAN KECIL (5.42 x 5.8 cm) ================= */
        <g transform="scale(0.775) translate(0, 0)">
          {renderLabelBody()}
        </g>
      )}
    </svg>
  );
}

/**
 * A4 Landscape Sheet Grid Component
 * Renders an A4 Landscape paper (297 x 210 mm)
 * - Besar: 4 columns x 2 rows = 8 labels
 * - Kecil: 5 columns x 3 rows = 15 labels
 * With red dashed cut guides matching Fadli's Illustrator screenshot.
 */
export default function LabelDusSheetGrid({
  boxesList = [],
  labelSize = 'besar',
  brandName,
  recipientName,
  recipientAddress,
  poNumber,
  variantName,
  tglProduksi,
  expDate,
  catatanDus,
  qrTargetUrl,
  sheetIndex = 0,
  totalSheets = 1,
  sheetRef,
  isPortraitMode = false
}) {
  const isBesar = labelSize === 'besar';
  const cols = isBesar ? 4 : 5;
  const rows = isBesar ? 2 : 3;
  const capacity = cols * rows;

  // Fill up to full capacity with empty placeholders so the red dashed cut grid remains complete
  const cells = useMemo(() => {
    const list = [...boxesList];
    while (list.length < capacity) {
      list.push(null); // empty cell
    }
    return list;
  }, [boxesList, capacity]);

  return (
    <div
      ref={sheetRef}
      className={`${isPortraitMode ? 'a4-portrait-sheet' : 'a4-landscape-sheet'} bg-white relative flex flex-col justify-center items-center shadow-lg print:shadow-none mx-auto overflow-hidden`}
      style={{
        width: isPortraitMode ? '210mm' : '297mm',
        height: isPortraitMode ? '297mm' : '210mm',
        backgroundColor: '#ffffff',
        boxSizing: 'border-box',
        padding: 0,
        pageBreakAfter: 'always',
        breakAfter: 'page',
        contain: 'strict',
      }}
    >
      <style type="text/css">{`
        @media print {
          .a4-landscape-sheet, .a4-portrait-sheet {
            page-break-after: always !important;
            break-after: page !important;
            box-shadow: none !important;
            margin: 0 !important;
            contain: strict !important;
          }
        }
      `}</style>

      {/* Grid Container with Red Dashed Cut Marks (dirotasi 90 derajat jika mode Portrait) */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isBesar ? 'repeat(4, 70mm)' : 'repeat(5, 54.2mm)',
          gridTemplateRows: isBesar ? 'repeat(2, 75mm)' : 'repeat(3, 58mm)',
          width: isBesar ? '280mm' : '271mm',
          height: isBesar ? '150mm' : '174mm',
          boxSizing: 'content-box',
          borderTop: '1px dashed #ef4444',
          borderLeft: '1px dashed #ef4444',
          transform: isPortraitMode ? 'rotate(90deg)' : 'none',
          transformOrigin: 'center center',
          willChange: isPortraitMode ? 'transform' : 'auto',
          contain: 'content',
        }}
      >
        {cells.map((box, idx) => (
          <div
            key={box ? box.boxIndex : `empty-${idx}`}
            style={{
              width: isBesar ? '70mm' : '54.2mm',
              height: isBesar ? '75mm' : '58mm',
              boxSizing: 'border-box',
              borderRight: '1px dashed #ef4444',
              borderBottom: '1px dashed #ef4444',
              position: 'relative',
              backgroundColor: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden'
            }}
          >
            {box ? (
              <div style={{ width: '100%', height: '100%' }}>
                <SingleLabelSVG
                  labelSize={labelSize}
                  brandName={brandName}
                  recipientName={recipientName}
                  recipientAddress={recipientAddress}
                  poNumber={poNumber}
                  variantName={box.variantName || variantName}
                  qtyPcs={box.qtyPcs}
                  tglProduksi={tglProduksi}
                  expDate={expDate}
                  dusKe={box.boxNumberStr}
                  catatanDus={box.catatanDus || catatanDus}
                  qrTargetUrl={qrTargetUrl}
                  isMixBox={box.isMixBox}
                  isLebihanBox={box.isLebihanBox}
                  breakdownItems={box.breakdownItems}
                />
              </div>
            ) : (
              // Empty Cell Cut Guide Placeholder
              <div className="w-full h-full flex flex-col items-center justify-center text-slate-300 select-none bg-slate-50/20">
                <span className="text-[7px] text-red-300 font-mono tracking-widest uppercase">Garis Potong</span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
